import posthog from 'posthog-js';

/**
 * PostHog analytics service.
 *
 * Anonymous visitors are never identified and autocapture stays off until a
 * user successfully logs in. In addition to autocapture we emit explicit, named
 * product-usage events — `login`, `logout`, and any `feature_used` / custom
 * events callers send via `track()` — that the atEaseAI usage webhook maps to
 * the matching CRM account by the person's email domain (no `internal` flag).
 */

const POSTHOG_TOKEN = process.env.REACT_APP_POSTHOG_TOKEN;
// Events API host. PostHog automatically serves its static assets (array.js,
// config.js) from a companion CDN host derived from this — e.g.
// `us-assets.i.posthog.com` for US cloud. That request is expected and correct,
// not an error; to serve assets from your own domain you'd add a reverse proxy.
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let isInitialized = false;

/**
 * Initializes the PostHog client. Only called once analytics consent has
 * been granted (see ConsentContext / useAnalyticsConsentBridge) — never at
 * unconditional app startup. Safe to call repeatedly: the first call
 * performs `posthog.init`, later calls (e.g. consent re-granted after a
 * withdrawal) just re-enable capturing via `opt_in_capturing`.
 * Does not identify the visitor and keeps autocapture disabled until login.
 */
const initialize = () => {
  if (!POSTHOG_TOKEN) return;

  if (isInitialized) {
    posthog.opt_in_capturing();
    return;
  }

  posthog.init(POSTHOG_TOKEN, {
    api_host: POSTHOG_HOST,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    property_denylist: [
      '$current_url',
      '$pathname',
      '$referrer',
      '$referring_domain',
    ],
    // Only signed-in users are tracked, so never create anonymous person profiles.
    person_profiles: 'identified_only',
  });

  isInitialized = true;
};

/**
 * Identifies the logged-in user and enables autocapture. Identity only — it does
 * NOT emit a `login` event, because it also runs on session restore (returning
 * users with a valid token in storage). Emit `login` explicitly at the real
 * login / signup call site via `track('login')`.
 *
 * Events are treated as external-client product usage: the backend webhook maps
 * them to the matching CRM account by email domain, so we attach `email` and
 * `organization_id` as person properties for segmentation (no `internal` flag).
 * @param {{ id: string, email?: string, organization_id?: string }} user
 */
const identifyUser = (user) => {
  if (!isInitialized || !user?.id) return;

  // Backend's UserResponse exposes the user's identifier as `id`.
  posthog.identify(user.id, {
    email: user.email,
    organization_id: user.organization_id,
  });

  posthog.set_config({ autocapture: true });
};

/**
 * Emit a named product-usage event (e.g. `login`, `logout`, `feature_used`,
 * `session_end`). No-op until PostHog is initialized. Property names should align
 * with the atEaseAI usage API — e.g. `feature_used` carries `feature_name`,
 * `session_end` carries `duration_seconds`.
 * @param {string} eventType
 * @param {Record<string, unknown>} [properties]
 */
const track = (eventType, properties = {}) => {
  if (!isInitialized || !eventType) return;
  posthog.capture(eventType, properties);
};

/**
 * Convenience for `feature_used` events. The feature name is matched to your
 * product catalog on ingest (ignoring case and separators).
 * @param {string} featureName
 * @param {Record<string, unknown>} [properties]
 */
const trackFeatureUsed = (featureName, properties = {}) => {
  if (!featureName) return;
  track('feature_used', { feature_name: featureName, ...properties });
};

/**
 * Clears all PostHog state on logout so the next login starts anonymous.
 * Emits a `logout` event first, while the user is still identified.
 */
const reset = () => {
  if (!isInitialized) return;
  track('logout');
  posthog.reset();
};

/**
 * Withdraws analytics consent at runtime: clears the identified person
 * (`reset`, so the next grant starts anonymous) and opts the client out of
 * capturing so no further events/requests are sent until consent is
 * re-granted (which calls `initialize()` again → `opt_in_capturing`).
 * No-op if PostHog was never initialized (consent was never granted).
 */
const disable = () => {
  if (!isInitialized) return;
  posthog.reset();
  posthog.opt_out_capturing();
};

const posthogAnalytics = { initialize, identifyUser, track, trackFeatureUsed, reset, disable };

export default posthogAnalytics;
